/* FASE 13 (WS4) — Página corporativa "For organisers".
   Contenido REAL del producto con el posicionamiento maestro: AOS Equestrian
   es la plataforma tecnológica; la subasta es SIEMPRE del organizador. */
import { CreditCard, Gavel, Globe, Layers, Percent, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { H_SECTION, PrimaryCta, SecondaryCta, SUBLINE } from "@/components/marketing/Sections";
import { BRAND } from "@/config/brand";
import { useLocalePath } from "@/lib/locale";
import { usePlatformPricing } from "@/hooks/usePlatformPricing";
import { PUBLIC_BASE, useJsonLd, usePageMeta } from "@/lib/seo";

const BLOCK_ICONS = [Globe, Layers, Gavel, Percent, CreditCard, ShieldCheck];

export default function ForOrganisers() {
  const { t } = useTranslation();
  const { fee, commission } = usePlatformPricing();
  const lp = useLocalePath();
  usePageMeta({
    title: `${t("fo.eyebrow")} — ${BRAND.name}`,
    description: t("fo.sub"),
    localized: true,
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${BRAND.name} — ${t("fo.eyebrow")}`,
    serviceType: "Horse auction software platform",
    description: t("fo.sub"),
    provider: { "@type": "Organization", name: BRAND.name, url: PUBLIC_BASE },
    audience: { "@type": "Audience", audienceType: "Horse auction organisers, studs, breeders, event organisers" },
  });

  return (
    <div data-testid="for-organisers-page">
      {/* Hero */}
      <section className="max-w-[980px] mx-auto px-6 pt-20 pb-16 text-center flex flex-col items-center gap-5">
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#6e6e73]">{t("fo.eyebrow")}</p>
        <h1 className={`${H_SECTION} text-[#1d1d1f]`} data-testid="fo-title">{t("fo.title")}</h1>
        <p className={`${SUBLINE} text-[#4b4b50] max-w-2xl`}>{t("fo.sub")}</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
          <PrimaryCta small to="/create" testid="fo-cta-create">{t("mk.cta")}</PrimaryCta>
          <SecondaryCta small to="/signup" testid="fo-cta-register">{t("fo.ctaRegister")}</SecondaryCta>
        </div>
      </section>

      {/* Bloques de producto */}
      <section className="bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto px-6 py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
          {[1, 2, 3, 4, 5, 6].map((n) => {
            const Icon = BLOCK_ICONS[n - 1];
            return (
              <div key={n} className="flex flex-col gap-3" data-testid={`fo-block-${n}`}>
                <Icon className="w-6 h-6 text-[#1d1d1f]" strokeWidth={1.5} aria-hidden="true" />
                <h2 className="text-[17px] font-semibold text-[#1d1d1f]">{t(`fo.b${n}t`)}</h2>
                <p className="text-sm text-[#3f3f44] leading-relaxed">{t(`fo.b${n}d`)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Platform Economics: importes desde Admin → Economics (fuente única) */}
      <section className="max-w-[780px] mx-auto px-6 py-20 text-center flex flex-col items-center gap-4">
        <h2 className="text-2xl lg:text-3xl font-semibold text-[#1d1d1f]">{t("fo.pricingTitle")}</h2>
        <p className="text-base text-[#3f3f44] leading-relaxed" data-testid="fo-pricing-note">
          {t("fo.pricingNote", { fee: fee ?? "…", commission: commission ?? "…" })}
        </p>
        <Link to={lp("/pricing")} data-testid="fo-pricing-link" className="text-sm font-semibold text-[#1d4d3b] hover:underline">
          {t("nav.pricing")} →
        </Link>
      </section>

      {/* CTA final */}
      <section className="bg-[#0A0A0A]">
        <div className="max-w-[780px] mx-auto px-6 py-20 text-center flex flex-col items-center gap-6">
          <h2 className="text-2xl lg:text-3xl font-semibold text-white">{t("fo.ctaTitle")}</h2>
          <SecondaryCta onDark to="/create" testid="fo-final-cta">{t("mk.cta")}</SecondaryCta>
        </div>
      </section>
    </div>
  );
}
